var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isNullOrUndefined } from '@syncfusion/ej2-base';
import { BlockType } from '../../models/enums';
import { decoupleReference, getAbsoluteOffset, isNodeAroundSpecialElements } from '../../common/utils/common';
import { findCellById, getBlockContentElement, getBlockModelById, isAtStartOfBlock } from '../../common/utils/block';
import { findClosestParent, isElementEmpty } from '../../common/utils/dom';
import { convertHtmlElementToBlocks, getBlockDataAsHTML, convertInlineElementsToContentModels, renderElementWithWrapper, renderContentAsHTML } from '../../common/utils/html-parser';
import { getSelectedRange, setCursorPosition } from '../../common/utils/selection';
import { ClipboardCleanupModule } from '../plugins/common/clipboard-cleanup';
import { actionType, events } from '../../common/constant';
import * as constants from '../../common/constant';
import { createBlocksFromPlainText, createCellsPayloadFromExternal, extractPlainTextMatrixFromPayload, generatePlainTextForExternalClipboard, htmlTableFromMatrix, isBlockLevelContent, tsvFromMatrix, unWrapContainer, writeTableClipboardPayload, buildTableClipboardPayload } from '../../common/utils/clipboard-utils';
import { BlockFactory } from '../../block-manager/services/block-factory';
import { doesHtmlHasTable, getDataCell, getSelectedCells, hasActiveTableSelection, toDomRow, toModelRow } from '../../common/utils/table-utils';
/* eslint-enable @typescript-eslint/no-misused-new, no-redeclare */
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB limit
/**
 * Handles clipboard operations (copy, cut, paste) for the Block Editor.
 */
export class ClipboardAction {
    constructor(manager) {
        this.parent = manager;
        this.clipboardCleanupModule = new ClipboardCleanupModule(this.parent);
        this.wireEvents();
    }
    wireEvents() {
        this.parent.observer.on(events.copy, this.handleCopy, this);
        this.parent.observer.on(events.cut, this.handleCut, this);
        this.parent.observer.on(events.paste, this.handlePaste, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    unwireEvents() {
        this.parent.observer.off(events.copy, this.handleCopy);
        this.parent.observer.off(events.cut, this.handleCut);
        this.parent.observer.off(events.paste, this.handlePaste);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    /**
     * Handles the cut operation.
     *
     * @param {ClipboardEvent} e - The clipboard event.
     * @returns {void}
     * @hidden
     */
    handleCut(e) {
        this.handleCopy(e);
        this.performCutOperation();
    }
    /**
     * Handles the copy operation.
     *
     * @param {ClipboardEvent} e - The clipboard event.
     * @returns {void}
     * @hidden
     */
    handleCopy(e) {
        e.preventDefault();
        const focusedEl = this.parent.currentFocusedBlock;
        const tableBlockEl = focusedEl && focusedEl.closest('.' + constants.TABLE_BLOCK_CLS);
        const range = getSelectedRange();
        const hasActiveSel = hasActiveTableSelection(tableBlockEl);
        if (tableBlockEl && (hasActiveSel || findClosestParent(range.startContainer, '.e-action-handle'))) {
            const { payload, html, plainText } = this.getTablePayload(tableBlockEl);
            writeTableClipboardPayload(e.clipboardData, payload, html, plainText);
            return;
        }
        const { html, text, blockeditorData } = this.getClipboardPayload();
        e.clipboardData.setData('text/html', html);
        e.clipboardData.setData('text/plain', text);
        e.clipboardData.setData('text/blockeditor', blockeditorData);
    }
    /**
     * Handles the paste operation.
     *
     * @param {ClipboardEvent} e - The clipboard event.
     * @returns {void}
     * @hidden
     */
    handlePaste(e) {
        e.preventDefault();
        this.performPasteOperation({
            blockeditorData: e.clipboardData.getData('text/blockeditor'),
            html: e.clipboardData.getData('text/html'),
            text: e.clipboardData.getData('text/plain'),
            file: this.extractFileFromClipboard(e)
        });
        this.parent.stateManager.updateManagerBlocks();
    }
    extractFileFromClipboard(e) {
        if (!e.clipboardData || !e.clipboardData.items || (e.clipboardData.items && e.clipboardData.items.length === 0)) {
            return null;
        }
        const items = e.clipboardData.items;
        const file = items[0].getAsFile();
        if (file !== null) {
            return file;
        }
        return !isNullOrUndefined(items[1]) ? items[1].getAsFile() : null;
    }
    /**
     * Gets the clipboard payload for the current selection.
     *
     * @returns {IClipboardPayloadOptions} - The clipboard payload containing HTML, text, and Block Editor data.
     * @hidden
     */
    getClipboardPayload() {
        if (getSelectedRange().toString().trim().length > 0) {
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(getSelectedRange().cloneContents());
            const selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
            const blocks = this.createPartialBlockModels(tempDiv, selectedBlocks);
            return {
                blockeditorData: selectedBlocks && (selectedBlocks.length > 1)
                    ? JSON.stringify({ blocks })
                    : JSON.stringify({ contents: this.createPartialContentModels(tempDiv) }),
                html: selectedBlocks && (selectedBlocks.length > 1)
                    ? getBlockDataAsHTML(blocks, this.parent.rootEditorElement.id)
                    : renderElementWithWrapper('span', renderContentAsHTML(this.createPartialContentModels(tempDiv))),
                text: generatePlainTextForExternalClipboard(blocks)
            };
        }
        const blockModel = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        const sanitizedBlock = decoupleReference(blockModel);
        return {
            blockeditorData: JSON.stringify({ block: sanitizedBlock }),
            html: getBlockDataAsHTML([blockModel], this.parent.rootEditorElement.id),
            text: generatePlainTextForExternalClipboard([blockModel])
        };
    }
    getTablePayload(tableBlockEl) {
        const blockModel = getBlockModelById(tableBlockEl.id, this.parent.getEditorBlocks());
        const payload = buildTableClipboardPayload(tableBlockEl, blockModel);
        const matrixText = extractPlainTextMatrixFromPayload(payload, blockModel);
        const html = htmlTableFromMatrix(matrixText, { hasHeader: payload.meta.enableHeader, hasRowNumbers: payload.meta.enableRowNumbers });
        const plainText = tsvFromMatrix(matrixText);
        return { payload, html, plainText };
    }
    createPartialBlockModels(selectionContainer, originalBlocks) {
        return originalBlocks.length === 0 ? [] : originalBlocks.map((block) => {
            const blockElement = selectionContainer.querySelector('.e-block#' + block.id);
            const contentElement = getBlockContentElement(blockElement) || selectionContainer;
            return BlockFactory.createBlockFromPartial(Object.assign({}, decoupleReference(block), { content: this.createPartialContentModels(contentElement) }));
        });
    }
    createPartialContentModels(selectionContainer) {
        return convertInlineElementsToContentModels(selectionContainer, true);
    }
    performCutOperation() {
        const tableCtx = this.parent.blockRenderer.tableRenderer.resolveTableContext();
        const range = getSelectedRange();
        if (range && range.toString().trim().length > 0) {
            this.performDeletionOperation(range);
        }
        else if (tableCtx) {
            this.performCellCut(tableCtx);
        }
        else {
            const blockElement = this.parent.currentFocusedBlock;
            const nextBlock = blockElement.nextElementSibling;
            const previousBlock = blockElement.previousElementSibling;
            this.parent.execCommand({ command: 'DeleteBlock', state: { blockElement: this.parent.currentFocusedBlock } });
            this.parent.setFocusToBlock(nextBlock || previousBlock);
        }
    }
    performPasteOperation(args) {
        const { blockeditorData, html, text, file } = args;
        // (Taking a snip in windows, automatically gets copied as file and not HTML. Hence below handling)
        if (file && (file.size < MAX_IMAGE_SIZE)) {
            this.parent.blockRenderer.imageRenderer.handleFilePaste(file);
            const url = URL.createObjectURL(file);
            URL.revokeObjectURL(url); // Immediately revoke to free memory
            return;
        }
        const beforePasteEventArgs = {
            cancel: false,
            content: text,
            callback: (args) => {
                if (args.cancel) {
                    return;
                }
                const range = getSelectedRange();
                if (range && range.toString().trim().length > 0) {
                    this.isSelectivePaste = true;
                    this.performDeletionOperation(range);
                }
                // Detect external HTML table when inside a table cell
                const tableCtx = this.parent.blockRenderer.tableRenderer.resolveTableContext();
                if (tableCtx && doesHtmlHasTable(html, text)) {
                    this.handleCellPasteInsideTable(tableCtx, html, text);
                    this.triggerAfterPasteEvent(text);
                    return;
                }
                const currentBlock = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
                if (currentBlock.blockType === BlockType.Code) {
                    this.handleCodeBlockContentPaste(text, currentBlock);
                    this.triggerAfterPasteEvent(text);
                    return;
                }
                if (blockeditorData) {
                    this.handleBlockEditorPaste(blockeditorData, text);
                    this.triggerAfterPasteEvent(text);
                    return;
                }
                const cleanedData = this.clipboardCleanupModule.cleanupPaste({ html: html, plainText: text });
                if (html && !this.parent.pasteCleanupSettings.plainText) {
                    this.handleHtmlPaste(cleanedData);
                }
                else if (text) {
                    this.handlePlainTextPaste(text);
                }
                this.triggerAfterPasteEvent(text);
                this.isSelectivePaste = false;
            }
        };
        this.parent.observer.notify('beforePaste', beforePasteEventArgs);
    }
    performDeletionOperation(range) {
        const selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
        if (selectedBlocks && selectedBlocks.length === 1) {
            const blockElement = this.parent.getBlockElementById(selectedBlocks[0].id);
            const contentElement = getBlockContentElement(blockElement);
            const newCursorPos = getAbsoluteOffset(contentElement, range.startContainer, range.startOffset);
            range.deleteContents();
            Array.from(contentElement.childNodes).forEach((node) => {
                const isNodeAroundMention = isNodeAroundSpecialElements(node);
                if (node.textContent.trim() === '' && !isNodeAroundMention) {
                    node.remove();
                }
            });
            this.parent.stateManager.updateContentOnUserTyping(blockElement);
            this.parent.setFocusAndUIForNewBlock(blockElement);
            setCursorPosition(contentElement, newCursorPos);
            const isCodeBlk = selectedBlocks[0].blockType === BlockType.Code;
            const isTableChild = this.parent.currentFocusedBlock.closest(`.${constants.TABLE_BLOCK_CLS}`);
            const isEmptyAfterDeletion = contentElement && contentElement.textContent.trim() === '';
            if (isEmptyAfterDeletion) {
                if (isCodeBlk) {
                    contentElement.textContent = '\n';
                }
                else if (!isNullOrUndefined(isTableChild)) {
                    contentElement.innerHTML = '<br>';
                }
            }
            return;
        }
        this.parent.blockCommand.handleSelectiveDeletions(new KeyboardEvent('keydown', { key: 'Backspace' }));
    }
    handleBlockEditorPaste(data, text) {
        try {
            const parsedData = JSON.parse(data);
            const props = Object.keys(parsedData)[0];
            switch (props) {
                case 'blocks':
                    this.handleMultiBlocksPaste(parsedData.blocks);
                    break;
                case 'block': {
                    const blocksToPaste = [decoupleReference(this.parent.blockService.generateNewIdsForBlock(parsedData.block))];
                    const cursorBlock = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
                    const parentBlock = getBlockModelById(cursorBlock.parentId, this.parent.getEditorBlocks());
                    if (parentBlock) {
                        this.parent.blockService.assignParentIdToBlocks(blocksToPaste, parentBlock.id);
                    }
                    this.parent.blockCommand.addBulkBlocks({
                        blocks: blocksToPaste,
                        targetBlockId: this.parent.currentFocusedBlock.id,
                        insertionType: 'block',
                        isSelectivePaste: this.isSelectivePaste
                    });
                    break;
                }
                case 'contents':
                    this.handleContentPasteWithinBlock(parsedData.contents);
                    break;
            }
        }
        catch (error) {
            console.error('Error parsing Block Editor clipboard data:', error);
            // Fallback to plain text paste
            this.handlePlainTextPaste(text);
        }
    }
    handleContentPasteWithinBlock(content) {
        if (!content.length) {
            return;
        }
        const range = this.parent.nodeSelection.getRange();
        if (!range) {
            return;
        }
        const cursorBlockElement = findClosestParent(range.startContainer, '.' + constants.BLOCK_CLS);
        const contentEle = getBlockContentElement(cursorBlockElement);
        const cursorBlock = getBlockModelById(cursorBlockElement.id, this.parent.getEditorBlocks());
        const oldBlock = decoupleReference(cursorBlock);
        const splitContent = this.parent.nodeCutter.splitContent(getBlockContentElement(cursorBlockElement), range.startContainer, range.startOffset);
        const beforeModels = this.parent.blockCommand.getContentModelForFragment(splitContent.beforeFragment);
        const afterModels = this.parent.blockCommand.getContentModelForFragment(splitContent.afterFragment);
        const pastedLength = content.reduce((sum, c) => sum + (c.content.length), 0);
        const targetOffset = getAbsoluteOffset(contentEle, range.startContainer, range.startOffset) + pastedLength;
        /* Update model */
        this.parent.blockService.updateContent(cursorBlock.id, [
            ...beforeModels,
            ...content,
            ...afterModels
        ]);
        /* Update DOM */
        this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                data: [{ block: cursorBlock, oldBlock: oldBlock }]
            } });
        setCursorPosition(contentEle, targetOffset);
        this.parent.undoRedoAction.pushActionIntoUndoStack({
            action: actionType.clipboardPaste,
            data: {
                type: 'content',
                oldContent: oldBlock.content,
                newContent: decoupleReference(cursorBlock.content),
                targetBlockId: cursorBlock.id,
                isSelectivePaste: this.isSelectivePaste,
                pasteEndOffset: targetOffset
            }
        });
    }
    /**
     * Handles multi-block paste operation.
     *
     * @param {BlockModel[]} blocks - The blocks to be pasted.
     * @param {boolean} isUndoRedoAction - Indicates if the action is part of an undo/redo operation.
     * @returns {void}
     * @hidden
     */
    handleMultiBlocksPaste(blocks, isUndoRedoAction = false) {
        if (!blocks.length) {
            return;
        }
        const range = this.parent.nodeSelection.getRange();
        if (!range) {
            return;
        }
        const editorBlocks = this.parent.getEditorBlocks();
        let cursorBlockElement = findClosestParent(range.startContainer, '.' + constants.BLOCK_CLS);
        const cursorBlock = getBlockModelById(cursorBlockElement.id, editorBlocks);
        const oldCursorBlock = decoupleReference(cursorBlock);
        const contentElement = getBlockContentElement(cursorBlockElement);
        const isContentEmpty = contentElement && isElementEmpty(contentElement);
        const isCursorAtStart = isAtStartOfBlock(contentElement);
        const parentBlock = getBlockModelById(cursorBlock.parentId, editorBlocks)
            || findCellById(cursorBlock.parentId, editorBlocks);
        const specialTypes = [BlockType.Table, BlockType.Image];
        const isFirstBlkSpecialType = specialTypes.indexOf(blocks[0].blockType) !== -1;
        let cursorBlockAfterSplit;
        let isFirstBlkProcessed = false;
        if (parentBlock) {
            this.parent.blockService.assignParentIdToBlocks(blocks, parentBlock.id);
        }
        if (isContentEmpty) {
            isFirstBlkProcessed = true;
            this.parent.blockService.generateNewIdsForBlock(blocks[0]);
            blocks[0].id = cursorBlockElement.id;
            this.parent.blockService.replaceBlock(cursorBlock.id, blocks[0]);
            this.parent.stateManager.updateManagerBlocks();
            this.parent.observer.notify('modelChanged', { type: 'ReplaceBlock', state: {
                    targetBlockId: cursorBlock.id,
                    block: getBlockModelById(blocks[0].id, this.parent.getEditorBlocks()),
                    oldBlock: oldCursorBlock,
                    preventEventTrigger: true
                } });
        }
        else if (!isFirstBlkSpecialType) {
            isFirstBlkProcessed = true;
            this.parent.execCommand({ command: 'SplitBlock', state: { preventEventTrigger: true } });
            /* When split at start, empty block will be appended before target */
            cursorBlockElement = ((range.startOffset === 0)
                ? cursorBlockElement.previousElementSibling
                : cursorBlockElement);
            const updatedCursorBlock = getBlockModelById(cursorBlockElement.id, this.parent.getEditorBlocks());
            if (!updatedCursorBlock) {
                return;
            }
            cursorBlockAfterSplit = decoupleReference(updatedCursorBlock);
            const isContentEmptyAfterSplit = !updatedCursorBlock.content || (updatedCursorBlock.content
                && updatedCursorBlock.content.length === 1 && updatedCursorBlock.content[0].content === '');
            const contentToUpdate = isContentEmptyAfterSplit ? [...blocks[0].content] : [
                ...updatedCursorBlock.content,
                ...blocks[0].content
            ];
            this.parent.blockService.updateContent(updatedCursorBlock.id, contentToUpdate);
            this.parent.observer.notify('modelChanged', { type: 'ReRenderBlockContent', state: {
                    data: [{ block: updatedCursorBlock, oldBlock: oldCursorBlock }],
                    preventEventTrigger: true
                } });
        }
        this.parent.blockCommand.addBulkBlocks({
            blocks: blocks.slice(!isFirstBlkProcessed ? 0 : 1)
                .map((block) => this.parent.blockService.generateNewIdsForBlock(block)),
            targetBlockId: cursorBlockElement.id,
            isUndoRedoAction: isUndoRedoAction,
            insertionType: 'blocks',
            clipboardBlocks: blocks.map((block) => decoupleReference(block)),
            cursorBlockAfterSplit: cursorBlockAfterSplit,
            oldBlockModel: oldCursorBlock,
            isPastedAtStart: isCursorAtStart,
            isSelectivePaste: this.isSelectivePaste
        });
        this.parent.observer.notify('triggerBlockChange', this.parent.eventService.getChanges());
    }
    handleCodeBlockContentPaste(content, blockModel) {
        const codeBlockContentElement = document.getElementById(blockModel.id).querySelector('.e-code-content');
        // Store the old block state for undo/redo tracking
        const oldBlockModel = decoupleReference(blockModel);
        // Get current cursor position
        const range = getSelectedRange();
        let cursorPosition = 0;
        if (range && range.startContainer === codeBlockContentElement
            || codeBlockContentElement.contains(range.startContainer)) {
            // Calculate cursor position from the beginning of text content
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(codeBlockContentElement);
            preCaretRange.setEnd(range.endContainer, range.endOffset);
            cursorPosition = preCaretRange.toString().length;
        }
        // Get current text content
        const currentText = codeBlockContentElement.textContent === '\n' ? '' : codeBlockContentElement.textContent;
        // Insert pasted content at cursor position
        const beforeCursor = currentText.substring(0, cursorPosition);
        const afterCursor = currentText.substring(cursorPosition);
        const newText = beforeCursor + content + afterCursor;
        // Update DOM
        codeBlockContentElement.textContent = newText;
        // Update model
        blockModel.content[0].content = newText;
        // Position cursor after pasted content
        const newCursorPosition = cursorPosition + content.length;
        setCursorPosition(codeBlockContentElement, newCursorPosition);
        // Track the change for undo/redo
        const clonedBlock = decoupleReference(blockModel);
        this.parent.undoRedoAction.trackContentChangedForUndoRedo(oldBlockModel, clonedBlock);
    }
    handleHtmlPaste(html) {
        const tempDiv = unWrapContainer((() => {
            const div = document.createElement('div');
            div.innerHTML = html;
            return div;
        })());
        if (isBlockLevelContent(tempDiv)) {
            this.handleMultiBlocksPaste(convertHtmlElementToBlocks(tempDiv, this.parent.pasteCleanupSettings.keepFormat));
        }
        else {
            this.handleContentPasteWithinBlock(convertInlineElementsToContentModels(tempDiv, this.parent.pasteCleanupSettings.keepFormat));
        }
    }
    handlePlainTextPaste(text) {
        this.handleMultiBlocksPaste(createBlocksFromPlainText(text));
    }
    handleCellPasteInsideTable(tableCtx, html, text) {
        const cleanedData = this.clipboardCleanupModule.cleanupPaste({ html: html, plainText: text });
        const payloadFromExternal = createCellsPayloadFromExternal(cleanedData, text);
        if (payloadFromExternal && payloadFromExternal.cells && payloadFromExternal.cells.length) {
            this.performCellPaste(payloadFromExternal, tableCtx);
            this.triggerAfterPasteEvent(text);
            this.isSelectivePaste = false;
            return;
        }
    }
    performCellPaste(payload, ctx) {
        if (!(payload.cells && payload.cells.length)) {
            return;
        }
        // Snapshot old blocks and track structure delta for undo/redo
        const oldNewPairs = [];
        const structureDelta = {};
        const needRows = Math.max(0, (ctx.startDataRow + payload.cells.length) - ctx.props.rows.length);
        if (needRows > 0) {
            structureDelta.rowsAdded = [];
            for (let i = 0; i < needRows; i++) {
                const at = ctx.props.rows.length;
                structureDelta.rowsAdded.push(at);
                this.parent.tableService.addRowAt({ blockId: ctx.tableBlockEl.id, rowIndex: at });
            }
        }
        const needCols = Math.max(0, (ctx.startDataCol + payload.cells[0].length) - ctx.props.columns.length);
        if (needCols > 0) {
            structureDelta.colsAdded = [];
            for (let i = 0; i < needCols; i++) {
                const at = ctx.props.columns.length;
                structureDelta.colsAdded.push(at);
                this.parent.tableService.addColumnAt({ blockId: ctx.tableBlockEl.id, colIndex: at });
            }
        }
        this.parent.tableService.removeCellFocus(ctx.tableEl);
        for (let r = 0; r < payload.cells.length; r++) {
            for (let c = 0; c < payload.cells[r].length; c++) {
                const targetRow = ctx.startDataRow + r;
                const targetCol = ctx.startDataCol + c;
                const oldBlocks = (ctx.props.rows[targetRow].cells[targetCol].blocks)
                    .map((b) => decoupleReference(b));
                const newBlocks = payload.cells[r][c];
                oldNewPairs.push({ dataRow: targetRow, dataCol: targetCol, oldBlocks, newBlocks });
                this.parent.tableService.setCellBlocks(ctx.tableEl, targetRow, targetCol, newBlocks);
                if (c === 0 && r === 0) {
                    const cellToFocus = getDataCell(ctx.tableEl, toDomRow(targetRow, ctx.props.enableHeader), targetCol);
                    this.parent.tableService.addCellFocus(cellToFocus, true);
                }
            }
        }
        // Push undo stack entry for table cells paste
        this.parent.undoRedoAction.trackTableCellsPasteForUndoRedo({
            blockId: ctx.tableBlockEl.id,
            cells: oldNewPairs,
            structureDelta
        });
    }
    performCellCut(tableCtx) {
        const selectedCells = Array.from(getSelectedCells(tableCtx.tableBlockEl));
        const tableEl = tableCtx.tableEl;
        const props = tableCtx.props;
        if (!selectedCells.length) {
            return;
        }
        const dataPositions = selectedCells.map((td) => ({
            r: props.enableHeader ? (parseInt(td.dataset.row, 10) - 1) : parseInt(td.dataset.row, 10),
            c: parseInt(td.dataset.col, 10)
        })).filter((p) => !isNaN(p.r) && !isNaN(p.c) && p.r >= 0 && p.c >= 0);
        const totalRows = props.rows.length;
        const totalCols = props.columns.length;
        const selectedKeySet = new Set(dataPositions.map((p) => `${p.r}:${p.c}`));
        // Build counts per row and per column
        const rowCounts = new Map();
        const colCounts = new Map();
        dataPositions.forEach((p) => {
            rowCounts.set(p.r, (rowCounts.get(p.r) || 0) + 1);
            colCounts.set(p.c, (colCounts.get(p.c) || 0) + 1);
        });
        const fullRows = Array.from(rowCounts.entries())
            .filter(([_, count]) => count === totalCols)
            .map(([r]) => r)
            .sort((a, b) => a - b);
        const onlyFullRowsSelected = fullRows.length > 0 && (fullRows.length * totalCols === selectedKeySet.size);
        const fullCols = Array.from(colCounts.entries())
            .filter(([_, count]) => count === totalRows)
            .map(([c]) => c)
            .sort((a, b) => a - b);
        const onlyFullColsSelected = fullCols.length > 0 && (fullCols.length * totalRows === selectedKeySet.size);
        if (onlyFullRowsSelected) {
            const rowsMeta = fullRows.map((r) => ({
                index: r,
                rowModel: decoupleReference(props.rows[r])
            }));
            // Push single undo entry for all rows
            this.parent.undoRedoAction.trackBulkRowDeletionForUndoRedo({
                blockId: tableCtx.tableBlockEl.id, rows: rowsMeta
            });
            const rowsToDeleteDom = fullRows
                .map((r) => (props.enableHeader ? r + 1 : r))
                .sort((a, b) => b - a);
            rowsToDeleteDom.forEach((domRowIdx) => this.parent.tableService.deleteRowAt({
                blockId: tableCtx.tableBlockEl.id,
                modelIndex: toModelRow(domRowIdx, props.enableHeader),
                isUndoRedoAction: true
            }));
            return;
        }
        if (onlyFullColsSelected) {
            const colsMeta = fullCols.map((c) => ({
                index: c,
                columnModel: decoupleReference(props.columns[c]),
                columnCells: props.rows.map((r) => decoupleReference(r.cells[c]))
            }));
            this.parent.undoRedoAction.trackBulkColumnDeletionForUndoRedo({
                blockId: tableCtx.tableBlockEl.id, cols: colsMeta
            });
            // Delete columns from right to left (data col indices)
            fullCols.sort((a, b) => b - a).forEach((dataColIdx) => this.parent.tableService.deleteColumnAt({
                blockId: tableCtx.tableBlockEl.id,
                colIndex: dataColIdx,
                isUndoRedoAction: true
            }));
            return;
        }
        // Partial selection: clear cell contents
        this.parent.tableService.clearCellContents(tableEl, selectedCells);
    }
    triggerAfterPasteEvent(text) {
        this.parent.observer.notify('afterPaste', { content: text });
    }
    /**
     * Checks if the clipboard is empty.
     *
     * @returns {Promise<boolean>} - A promise that resolves to true if the clipboard is empty, false otherwise.
     * @hidden
     */
    isClipboardEmpty() {
        return __awaiter(this, void 0, void 0, function* () {
            const clipboardItems = yield navigator.clipboard.read();
            return clipboardItems.length === 0;
        });
    }
    /**
     * Handles the context copy operation.
     *
     * @returns {Promise<void>} - A promise that resolves when the copy operation is complete.
     * @hidden
     */
    handleContextCopy() {
        return __awaiter(this, void 0, void 0, function* () {
            const { html, text } = this.getClipboardPayload();
            yield navigator.clipboard.write([
                new ClipboardItem({
                    'text/html': new Blob([html], { type: 'text/html' }),
                    'text/plain': new Blob([text], { type: 'text/plain' })
                })
            ]);
        });
    }
    /**
     * Handles the context cut operation.
     *
     * @returns {Promise<void>} - A promise that resolves when the cut operation is complete.
     * @hidden
     */
    handleContextCut() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.handleContextCopy();
            this.performCutOperation();
        });
    }
    /**
     * Handles the context paste operation.
     *
     * @returns {Promise<void>} - A promise that resolves when the paste operation is complete.
     * @hidden
     */
    handleContextPaste() {
        return __awaiter(this, void 0, void 0, function* () {
            let html = '';
            let text = '';
            let file;
            try {
                const clipboardItems = yield navigator.clipboard.read();
                for (const item of clipboardItems) {
                    if (item.types.indexOf('text/html') !== -1) {
                        html = yield (yield item.getType('text/html')).text();
                    }
                    if (item.types.indexOf('text/plain') !== -1) {
                        text = yield (yield item.getType('text/plain')).text();
                    }
                    if (item.types.indexOf('image/png') !== -1) {
                        file = yield item.getType('image/png');
                    }
                }
            }
            catch (err) {
                // Fallback for insecure context
                text = yield navigator.clipboard.readText();
            }
            this.performPasteOperation({ html, text, file: file });
        });
    }
    destroy() {
        this.unwireEvents();
        this.clipboardCleanupModule = null;
    }
}
